import { createFileRoute, useNavigate } from '@tanstack/solid-router';
import { For, Show, createMemo, createSignal, onMount } from 'solid-js';

import Header from '~/components/layout/Header';
import {
    OFFER_TYPE,
    type OfferInput,
    PRODUCT,
    TENDER_SELECTION
} from '~/lib/graphql/types';
import { trackSelectOffer } from '~/lib/helpers/analytics';
import { countryCode } from '~/lib/i18n';
import { selectOfferFn } from '~/lib/server/graphql';
import { useCalculator } from '~/lib/store';

export const Route = createFileRoute('/$locale/offer_/select')({
    component: OfferSelectPage
});

// ---------------------------------------------------------------------------
// Offer type configs
// ---------------------------------------------------------------------------

interface OfferOption {
    label: string;
    description: string;
    product: PRODUCT;
    term?: TENDER_SELECTION;
}

const offerOptions: OfferOption[] = [
    {
        label: 'Upfront Purchase',
        description: 'Buy the system outright and own it immediately.',
        product: PRODUCT.DP
    },
    {
        label: 'Power Purchase Agreement (PPA)',
        description: 'Pay only for the energy produced. No upfront cost.',
        product: PRODUCT.PPA
    },
    {
        label: '5-Year Rent-to-Own',
        description: 'Low monthly payments for 5 years, then own the system.',
        product: PRODUCT.RTO,
        term: TENDER_SELECTION.FIVE_YEAR
    },
    {
        label: '10-Year Rent-to-Own',
        description: 'Even lower monthly payments over 10 years.',
        product: PRODUCT.RTO,
        term: TENDER_SELECTION.TEN_YEAR
    }
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function OfferSelectPage() {
    const [state, actions] = useCalculator();
    const navigate = useNavigate();

    const [selectedIdx, setSelectedIdx] = createSignal<number | null>(null);
    const [submitting, setSubmitting] = createSignal(false);
    const [success, setSuccess] = createSignal(false);
    const [errorMsg, setErrorMsg] = createSignal<string | null>(null);

    onMount(() => {
        if (!state.offer?.code) {
            navigate({ to: '/$locale', search: (prev) => prev });
        }
    });

    const offer = createMemo(() => state.offer);

    // Filter options to only show applicable ones
    const applicableOptions = createMemo(() => {
        const types = offer()?.applicableOfferTypes ?? [];
        return offerOptions.filter((opt) => {
            if (opt.product === PRODUCT.DP)
                return types.includes(OFFER_TYPE.UPFRONT);
            if (opt.product === PRODUCT.PPA)
                return types.includes(OFFER_TYPE.PPA);
            if (
                opt.product === PRODUCT.RTO &&
                opt.term === TENDER_SELECTION.FIVE_YEAR
            )
                return types.includes(OFFER_TYPE.FIVE_YEAR_RTO);
            if (
                opt.product === PRODUCT.RTO &&
                opt.term === TENDER_SELECTION.TEN_YEAR
            )
                return types.includes(OFFER_TYPE.TEN_YEAR_RTO);
            return true;
        });
    });

    async function handleConfirm() {
        const idx = selectedIdx();
        if (idx === null) return;
        const selected = applicableOptions()[idx];
        if (!selected || !offer()?.code) return;

        setSubmitting(true);
        setErrorMsg(null);

        try {
            const input: OfferInput = {
                code: offer()!.code,
                product: selected.product,
                term: selected.term ?? null
            };

            await selectOfferFn({
                data: {
                    country_code: countryCode(),
                    input
                }
            });

            trackSelectOffer({
                type: state.quote.type ?? null,
                code: offer()!.code,
                option: selected.product
            });
            setSuccess(true);
            setSubmitting(false);
        } catch (err) {
            setSubmitting(false);
            const message =
                err instanceof Error ? err.message : 'Something went wrong';
            setErrorMsg(message);
        }
    }

    return (
        <div class="min-h-screen bg-white">
            {/* Header */}
            <Header />

            <div class="max-w-xl mx-auto px-4 py-8 sm:py-12">
                {/* Success State */}
                <Show when={success()}>
                    <div class="text-center py-12">
                        <div class="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
                            <svg
                                class="w-8 h-8 text-green-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24">
                                <path
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    stroke-width="2"
                                    d="M5 13l4 4L19 7"
                                />
                            </svg>
                        </div>
                        <h1 class="text-2xl sm:text-3xl font-heading font-bold text-brand-black">
                            You're all set!
                        </h1>
                        <p class="mt-3 text-gray-500 max-w-sm mx-auto">
                            Your offer selection has been confirmed. Our team
                            will be in touch shortly with the next steps.
                        </p>
                        <button
                            type="button"
                            onClick={() => {
                                actions.reset();
                                navigate({
                                    to: '/$locale',
                                    search: (prev) => prev
                                });
                            }}
                            class="mt-8 px-8 py-3 rounded-full bg-brand-yellow text-brand-black font-semibold text-sm hover:opacity-90 transition-opacity">
                            Start a new calculation
                        </button>
                    </div>
                </Show>

                {/* Selection State */}
                <Show when={!success()}>
                    <h1 class="text-2xl sm:text-3xl font-heading font-bold text-brand-black text-center">
                        Select Your Offer
                    </h1>
                    <p class="mt-2 text-sm text-gray-500 text-center">
                        Choose the option that works best for you.
                    </p>

                    {/* Offer summary */}
                    <Show when={offer()}>
                        {(offerData) => (
                            <div class="mt-6 bg-gray-50 rounded-xl p-4 text-sm">
                                <div class="flex justify-between">
                                    <span class="text-gray-500">
                                        System size
                                    </span>
                                    <span class="font-semibold">
                                        {offerData().installation.size} kWp
                                    </span>
                                </div>
                                <div class="flex justify-between mt-1.5">
                                    <span class="text-gray-500">
                                        Offer code
                                    </span>
                                    <span class="font-mono text-xs text-gray-600">
                                        {offerData().code}
                                    </span>
                                </div>
                            </div>
                        )}
                    </Show>

                    {/* Offer options */}
                    <div class="mt-8 space-y-3">
                        <For each={applicableOptions()}>
                            {(option, idx) => (
                                <button
                                    type="button"
                                    class={`w-full text-left p-5 rounded-xl border-2 transition-colors ${
                                        selectedIdx() === idx()
                                            ? 'border-brand-blue bg-brand-blue/25'
                                            : 'border-brand-blue/25 bg-white hover:border-brand-blue/50'
                                    }`}
                                    onClick={() => setSelectedIdx(idx())}>
                                    <div class="flex items-center gap-3">
                                        <div
                                            class={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                                selectedIdx() === idx()
                                                    ? 'border-brand-blue'
                                                    : 'border-gray-300'
                                            }`}>
                                            <Show
                                                when={selectedIdx() === idx()}>
                                                <div class="w-2.5 h-2.5 rounded-full bg-brand-blue" />
                                            </Show>
                                        </div>
                                        <div>
                                            <p class="font-semibold text-brand-black">
                                                {option.label}
                                            </p>
                                            <p class="text-sm text-gray-500 mt-0.5">
                                                {option.description}
                                            </p>
                                        </div>
                                    </div>
                                </button>
                            )}
                        </For>
                    </div>

                    {/* Error */}
                    <Show when={errorMsg()}>
                        <p class="text-sm text-red-600 mt-4">{errorMsg()}</p>
                    </Show>

                    {/* Confirm */}
                    <div class="mt-8">
                        <button
                            type="button"
                            onClick={handleConfirm}
                            disabled={selectedIdx() === null || submitting()}
                            class="w-full py-3.5 rounded-full bg-brand-yellow text-brand-black font-semibold text-base transition-opacity disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90">
                            <Show
                                when={submitting()}
                                fallback="Confirm Selection">
                                <span class="flex items-center justify-center gap-2">
                                    <svg
                                        class="animate-spin h-4 w-4"
                                        fill="none"
                                        viewBox="0 0 24 24">
                                        <circle
                                            class="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            stroke-width="4"
                                        />
                                        <path
                                            class="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        />
                                    </svg>
                                    Confirming...
                                </span>
                            </Show>
                        </button>
                    </div>
                </Show>
            </div>
        </div>
    );
}
